import { EmptyState } from '@workspace/ui/components/empty-state'
import { useEffect } from 'react'
import { trackEvent } from '../../../lib/gtag'
import { useAiReadsQuery } from '../hooks/use-investor-signals'
import { sectionAiReadLines } from '../lib/ai-reads-groups'

// Draft wording (DRAFT_WORDING_NOT_APPROVED.md, F3), awaiting David.
const formatSince = ({ since, sinceUpdate }: { since: string; sinceUpdate: boolean }) => {
  const day = new Date(`${since}T00:00:00Z`).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
  return sinceUpdate ? `Since your update on ${day}` : `Since ${day}`
}

/**
 * Anonymous lines about how investors' AI assistants used the record. The server decides which lines
 * exist and what they say; this only groups and shows them. A line is never tied to a person or a
 * fund. A line stays until it ages out or a later record answers it, and is shown under the record it was
 * read against.
 */
export function AiReadsPanel({ startupId }: { startupId: string }) {
  const { data, isLoading } = useAiReadsQuery(startupId)
  const lineCount = data?.data.lines.length
  useEffect(() => {
    if (lineCount !== undefined) {
      trackEvent('ai_reads_panel_viewed', { startup_id: startupId, line_count: lineCount })
    }
  }, [startupId, lineCount])
  if (isLoading || !data) return null
  const sections = sectionAiReadLines(data.data.lines)

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          How investors' AI used your record
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{formatSince(data.data)}</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
          <li>
            What investors' AI assistants looked at, could not find, and would come back for, by
            kind. Never a name, a fund or their words.
          </li>
          <li>
            "Would look again" lines say what investors are waiting to see on your record, so they
            show you what to add.
          </li>
          <li>
            "Found no text" means your record does not yet state it in words an AI can find. A dated
            statement in your own words fixes that.
          </li>
          <li>A line shows about a day after it happens.</li>
          <li>
            Investors can opt out of this feedback at any time, so these lines show only part of
            what their AI did.
          </li>
        </ul>
      </div>
      {sections.length === 0 ? (
        <EmptyState title="Nothing to show yet" description="Nothing has been published yet." />
      ) : (
        sections.map((section) => (
          <div key={section.key} className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">{section.heading}</h3>
            {section.groups.map((entry) => (
              <div key={entry.group} className="rounded-xl border border-border p-4">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {entry.heading}
                </p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                  {entry.lines.map((line) => (
                    <li key={line.key}>{line.text}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))
      )}
    </section>
  )
}
